import { createContext, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Calendar, ChevronDown, Clock, Loader2, Lock, Mail, Phone, TrendingUp, User } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Wordmark } from "@/components/site/Layout";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BENEFIT_ICONS = [Calendar, Clock, TrendingUp];
const ROOM_KEYS = ["rooms_1", "rooms_2", "rooms_3", "rooms_4"];
const EMPTY = { firstname: "", lastname: "", email: "", phone: "", establishment: "", rooms: "", message: "", website: "" };

const DemoModalContext = createContext(() => {});
export const useDemoModal = () => useContext(DemoModalContext);

const inputCls =
  "w-full rounded-lg border border-ostelo-deep/15 bg-white py-3 pl-11 pr-4 text-sm text-ostelo-deep outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ostelo-ink/40 focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15";

const IconField = ({ icon: Icon, children }) => (
  <div className="relative">
    <Icon size={17} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ostelo-ink/35" aria-hidden="true" />
    {children}
  </div>
);

const DemoDialog = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const lang = useLang();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const benefits = t("demo_modal.benefits", { returnObjects: true });
  const accentFirst = t("demo_modal.title_accent_first") === "yes";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/demo`, {
        name: `${form.firstname.trim()} ${form.lastname.trim()}`.trim(),
        email: form.email.trim(),
        organization: form.establishment.trim(),
        establishment: form.establishment.trim(),
        rooms: form.rooms ? t(`demo_modal.${form.rooms}`) : null,
        phone: form.phone.trim() || null,
        message: form.message.trim() || t("demo_modal.message_empty"),
        lang,
        privacyAccepted: true,
        website: form.website,
      });
      toast.success(t("form.success_title"));
      setForm(EMPTY);
      onOpenChange(false);
    } catch {
      toast.error(t("form.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="demo-modal"
        onOpenAutoFocus={(e) => {
          /* Mobile : aucun focus automatique pour ne jamais ouvrir le clavier à l'ouverture */
          if (window.matchMedia("(max-width: 767.98px)").matches) e.preventDefault();
        }}
        className="grid max-h-[92vh] w-[calc(100vw-2rem)] max-w-5xl grid-cols-1 gap-0 overflow-y-auto rounded-2xl border-0 bg-white p-0 shadow-[0_40px_120px_rgb(12,27,71,0.35)] lg:grid-cols-[1fr_1.1fr]"
      >
        <DialogTitle className="sr-only">{t("demo_modal.a11y_title")}</DialogTitle>
        <DialogDescription className="sr-only">{t("demo_modal.form_sub")}</DialogDescription>

        {/* Colonne gauche */}
        <div className="flex flex-col p-8 lg:p-10">
          <Wordmark />
          <h2 className="mt-8 font-display text-[1.9rem] font-extrabold leading-[1.15] tracking-tight text-ostelo-deep">
            {t("demo_modal.title_1")}
            <br />
            {accentFirst ? (
              <>
                <span className="text-ostelo-blue">{t("demo_modal.title_w1")}</span> {t("demo_modal.title_w2")}
              </>
            ) : (
              <>
                {t("demo_modal.title_w1")} <span className="text-ostelo-blue">{t("demo_modal.title_w2")}</span>
              </>
            )}
          </h2>
          <span className="mt-4 block h-[3px] w-10 rounded-full bg-ostelo-blue" aria-hidden="true" />
          <p className="mt-5 max-w-md text-sm leading-relaxed text-ostelo-ink/65">{t("demo_modal.intro")}</p>
          <ul className="mt-7 space-y-5">
            {benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i];
              return (
                <li key={b.title} className="flex items-start gap-4" data-testid={`demo-benefit-${i}`}>
                  <Icon size={24} strokeWidth={1.8} className="mt-0.5 shrink-0 text-ostelo-blue" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-bold text-ostelo-deep">{b.title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-ostelo-ink/60">{b.desc}</p>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="mt-8 hidden lg:mt-auto lg:block lg:pt-8">
            <img
              src="/assets/demo/terrace-960.webp"
              srcSet="/assets/demo/terrace-640.webp 640w, /assets/demo/terrace-960.webp 960w"
              sizes="420px"
              width="960"
              height="540"
              loading="lazy"
              alt={t("demo_modal.photo_alt")}
              className="h-52 w-full rounded-xl object-cover"
              data-testid="demo-modal-photo"
            />
          </div>
        </div>

        {/* Colonne droite — formulaire */}
        <div className="border-t border-ostelo-deep/10 p-8 lg:border-l lg:border-t-0 lg:p-10">
          <h3 className="font-display text-xl font-extrabold tracking-tight text-ostelo-deep">{t("demo_modal.form_title")}</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-ostelo-ink/60">{t("demo_modal.form_sub")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="demo-modal-form">
            <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <IconField icon={User}>
                <input required minLength={2} value={form.firstname} onChange={set("firstname")} placeholder={t("demo_modal.firstname")} className={inputCls} data-testid="demo-modal-firstname" aria-label={t("demo_modal.firstname")} />
              </IconField>
              <IconField icon={User}>
                <input required minLength={2} value={form.lastname} onChange={set("lastname")} placeholder={t("demo_modal.lastname")} className={inputCls} data-testid="demo-modal-lastname" aria-label={t("demo_modal.lastname")} />
              </IconField>
            </div>
            <IconField icon={Mail}>
              <input required type="email" value={form.email} onChange={set("email")} placeholder={t("demo_modal.email")} className={inputCls} data-testid="demo-modal-email" aria-label={t("demo_modal.email")} />
            </IconField>
            <IconField icon={Phone}>
              <input type="tel" value={form.phone} onChange={set("phone")} placeholder={t("demo_modal.phone")} className={inputCls} data-testid="demo-modal-phone" aria-label={t("demo_modal.phone")} />
            </IconField>
            <IconField icon={Building2}>
              <input required minLength={2} value={form.establishment} onChange={set("establishment")} placeholder={t("demo_modal.establishment")} className={inputCls} data-testid="demo-modal-establishment" aria-label={t("demo_modal.establishment")} />
            </IconField>
            <div className="relative">
              <select required value={form.rooms} onChange={set("rooms")} className={`${inputCls} appearance-none px-4 pr-11 ${form.rooms ? "" : "text-ostelo-ink/40"}`} data-testid="demo-modal-rooms" aria-label={t("demo_modal.rooms")}>
                <option value="" disabled>{t("demo_modal.rooms")}</option>
                {ROOM_KEYS.map((k) => (
                  <option key={k} value={k}>{t(`demo_modal.${k}`)}</option>
                ))}
              </select>
              <ChevronDown size={17} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-ostelo-ink/35" aria-hidden="true" />
            </div>
            <textarea rows={3} value={form.message} onChange={set("message")} placeholder={t("demo_modal.message")} className="w-full resize-none rounded-lg border border-ostelo-deep/15 bg-white px-4 py-3 text-sm text-ostelo-deep outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ostelo-ink/40 focus:border-ostelo-blue focus:ring-2 focus:ring-ostelo-blue/15" data-testid="demo-modal-message" aria-label={t("demo_modal.message")} />
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2.5 rounded-lg bg-ostelo-blue py-3.5 text-sm font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_30px_rgb(36,81,232,0.3)] transition-[background-color,transform] duration-300 hover:scale-[0.99] hover:bg-[#1D43C4] active:scale-[0.97] disabled:opacity-60"
              data-testid="demo-modal-submit"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <Calendar size={17} />}
              {t("demo_modal.submit")}
            </button>
            <p className="flex items-center justify-center gap-1.5 pt-1 text-center text-xs text-ostelo-ink/50">
              <Lock size={12} aria-hidden="true" />
              {t("demo_modal.confidential")}
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DemoModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openDemo = useCallback(() => setOpen(true), []);
  return (
    <DemoModalContext.Provider value={openDemo}>
      {children}
      <DemoDialog open={open} onOpenChange={setOpen} />
    </DemoModalContext.Provider>
  );
};
